import pick from 'lodash/pick';

import {getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {PropertyType} from 'common/widgets/property-filter-menu/models/types';
import {STRING_TYPECAST_PROPERTIES} from 'common/widgets/property-filter-menu/models/utils';
import {BuilderPaneBase} from 'common/widgets/cohorts-query-builder/common-components/builder-pane-base';
import {SHOW_MORE_LIMIT} from 'common/widgets/cohorts-query-builder/models/constants';
import {DISABLED_DUE_TO_CLASSIFIED_DATA} from 'common/report/constants';
import {PropertyTab} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {Property} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {
  createDataGroupTabData,
  createPrimitiveTabData,
  getIconForPropertyType,
  isDefinedValue,
  joinKeyPropertyFromStagedDimension,
  renameProperty,
} from 'common/widgets/cohorts-query-builder/util';
import {isCohortLiteral, isEventLiteral} from 'common/widgets/cohorts-query-builder/util/staged-cohort';

import '../builder-pane-filter-property';
import template from './index.jade';
import css from './index.styl?inline';

class CohortsBuilderPropertyFilter extends BuilderPaneBase {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'pane-visible': `boolean`,
    };
  }

  shouldUpdate(state) {
    if (!state.stagedCohort) {
      return false;
    }

    const literal = this.getLiteral(state);
    return (
      (isEventLiteral(literal) || isCohortLiteral(literal)) &&
      isDefinedValue(state[this.builderStateReference].propertyFilterIdx)
    );
  }

  get config() {
    return {
      ...super.config,
      useShadowDom: true,
      css,
      template,
      helpers: {
        ...super.config.helpers,
        pick,
        PropertyTab,
        clickedItem: (ev) => {
          const {item} = ev.detail;
          const {name, propertyType, joinPropertyType, dimensionDataGroupId, dataGroupId, customPropertyId} = item;
          const type = STRING_TYPECAST_PROPERTIES.includes(name) ? PropertyType.String : item.type;

          this.dispatchBubbledEvent(`selected-property`, {
            name,
            type,
            propertyType,
            joinPropertyType,
            dimensionDataGroupId: customPropertyId ? item.customProperty.mappedDataGroupId : dimensionDataGroupId,
            dataGroupId,
            customPropertyId,
          });

          if (type === PropertyType.Dimension) {
            this.app.updateBuilderState({
              searchFilter: ``,
              stagedDimension: item,
            });
            // Recurse screens for dimension
            this.nextPane(`cohorts-builder-property-filter`);
          } else {
            this.app.updateBuilderState({
              stagedDimension: null,
            });
            this.nextPane(`cohorts-builder-pane-filter-property`);
          }
        },
        showItemDetails: (ev) => this.showItemDetails(ev.detail.item),
        handlePropertyTabToggleChange: (ev) => {
          const selectedTab = this.helpers.getPropertyTabs().find((tab) => tab.value === ev.detail.selected);
          this.updateBuilder({propertyTab: ev.detail.selected, propertyTabType: selectedTab.type});
        },
        hasPropertyTabToggle: () => {
          return this.helpers.getPropertyTabs().length > 1;
        },
        getPropertyTabs: () => {
          const literal = this.getLiteral();
          const isAnalyzingUser = this.app.isAnalyzingUser();
          if (isEventLiteral(literal)) {
            return [createPrimitiveTabData(PropertyTab.Events)];
          } else if (isCohortLiteral(literal)) {
            return [
              ...(this.app.hasMultiplePeopleGroups() ? [createPrimitiveTabData(PropertyTab.All)] : []),
              ...(isAnalyzingUser ? [createPrimitiveTabData(PropertyTab.People)] : []),
              ...(this.app.hasDataGroups()
                ? this.state.dataGroups.map((dataGroup) => createDataGroupTabData(dataGroup))
                : []),
            ];
          } else {
            throw new Error(`Unexpected literal type`);
          }
        },
        getSections: () => {
          const {propertyTabType, propertyTab} = this.builderState;
          if (propertyTabType === PropertyTab.All) {
            return this.helpers
              .getPropertyTabs()
              .filter((tab) => tab.type !== PropertyTab.All)
              .map((tab) => this.getTabSection(tab.type, tab.value));
          }
          return [this.getTabSection(propertyTabType, propertyTab)];
        },
        isLoadingProperties: () => this.state.loading.eventProperties.get(this.getSelectedEventName()),
      },
    };
  }

  showItemDetails(item) {
    let tooltipData = null;
    const {propertyType, dataGroupId} = item;
    // Don't show lexicon tooltip when it's a joined datagroup property or if there's a global datagroup selected
    if (!dataGroupId && !this.app.getDataGroupId()) {
      let propertyDefinitions = null;
      if (propertyType === Property.Event) {
        propertyDefinitions = this.state.eventPropertyDefinitions;
      } else {
        propertyDefinitions = this.state.userPropertyDefinitions;
      }
      const mpProperty = pick(item, [`name`, `customPropertyId`, `customProperty`, `isDisabled`]);
      if (mpProperty.customPropertyId || mpProperty.customProperty) {
        mpProperty.actionDisabled = true;
      }
      tooltipData = !item.isDisabled
        ? getTooltipDataForProperty(mpProperty, propertyDefinitions, {lookupTables: this.state.lookupTables})
        : null;
    }
    this.update({tooltipData});
  }

  getSelectedPropertyName() {
    const {propertyFilterIdx} = this.builderState;
    return this.state.stagedCohort.getPropertyName(this.state.stagedClausePosition, propertyFilterIdx);
  }

  getSelectedEventName() {
    return this.state.stagedCohort.getFrequencyClauseEventValue(this.state.stagedClausePosition);
  }

  getSelectedDataGroup(dataGroupId) {
    return this.state.dataGroups.find((dataGroup) => dataGroup.groupID === dataGroupId);
  }

  getEventsSection() {
    const eventName = this.getSelectedEventName() ?? ``;
    const properties = this.state.eventProperties.get(eventName);
    const isLoading = this.state.loading.eventProperties.get(eventName);
    const selectedPropertyName = this.getSelectedPropertyName();

    if (!this.state.eventProperties.has(eventName) && !isLoading) {
      this.app.fetchEventProperties(eventName);
    }

    return {
      isLoading,
      items: this.getPropertyItems(properties || [], Property.Event, selectedPropertyName),
      label: `Event Properties`,
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  getDataGroupSection(selectedDataGroup) {
    return {
      items: this.getPropertyItems(
        selectedDataGroup.properties || [],
        Property.User,
        this.getSelectedPropertyName(),
        selectedDataGroup.groupID,
      ),
      label: selectedDataGroup.displayName,
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  getDimensionSection() {
    const selectedPropertyName = this.getSelectedPropertyName();
    const stagedDimension = this.builderState.stagedDimension;

    const joinKeyItem = joinKeyPropertyFromStagedDimension(stagedDimension);
    const properties = [joinKeyItem, ...(this.state.dimensionProperties || [])];

    return {
      isLoading: this.state.loading.dimensions,
      items: this.getPropertyItems(properties, stagedDimension.propertyType, selectedPropertyName),
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  getPeopleSection() {
    const selectedPropertyName = this.getSelectedPropertyName();
    return {
      isLoading: this.state.loading.peopleProperties,
      items: this.getPropertyItems(this.state.peopleProperties || [], Property.User, selectedPropertyName),
      label: `Profile Properties`,
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  getTabSection(type, value) {
    if (this.builderState.stagedDimension) {
      return this.getDimensionSection();
    }

    switch (type) {
      case PropertyTab.Events:
        return this.getEventsSection();
      case PropertyTab.People:
        return this.getPeopleSection();
      case PropertyTab.DataGroup:
        return this.getDataGroupSection(this.getSelectedDataGroup(value));
    }
  }

  getLiteral(state = this.state) {
    const {literalIdx} = state.stagedClausePosition;
    return state.stagedCohort.literals[literalIdx];
  }

  getPropertyItems(properties, propertyType, selectedProperty, dataGroupId = null) {
    return properties.map((ev) => ({
      propertyType,
      label: renameProperty(ev.name, {dataGroupId}),
      icon: getIconForPropertyType(ev.type),
      hasCaret: !ev.sensitive,
      isDisabled: ev.sensitive,
      tooltip: ev.sensitive ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null,
      isSelected: ev.name === selectedProperty,
      dataGroupId,
      dimensionDataGroupId: ev.dimensionDataGroupId,
      ...ev,
    }));
  }
}

export const CohortsBuilderPropertyFilterEl = defineAndMakeJsxFactory(
  `cohorts-builder-property-filter`,
  CohortsBuilderPropertyFilter,
);
